import * as stylex from '@stylexjs/stylex'
import { type ElementType, type ReactNode } from 'react'
import { Box, type BoxProps } from '../Box/Box'
import { colors } from '../tokens/colors.stylex'
import { elevationBottom, elevationTop } from '../tokens/elevation.stylex'

const shadowVariants = stylex.create({
  top: (elevation: ElevationLevel) => ({
    boxShadow: elevationTop[`elevation${elevation}`],
  }),
  bottom: (elevation: ElevationLevel) => ({
    boxShadow: elevationBottom[`elevation${elevation}`],
  }),
})

const baseStyles = stylex.create({
  root: {
    backgroundColor: colors.white,
    position: 'relative',
  },
  border: {
    borderColor: colors.grey400,
    borderStyle: 'solid',
    borderWidth: 1,
  },
  highlighted: {
    borderLeftColor: colors.primary600,
    borderLeftStyle: 'solid',
    borderLeftWidth: 4,
  },
  mask: (color: string, borderRadius: PaperProps['borderRadius']) => ({
    borderRadius,
    backgroundColor: color,
    position: 'absolute',
    zIndex: 10,
    height: '100%',
    left: 0,
    top: 0,
    width: '100%',
  }),
})

export type ElevationLevel = 0 | 2 | 4 | 6 | 8 | 12 | 16

export type ShadowDirection = 'top' | 'bottom'

export type PaperProps<RootComponent extends ElementType = 'div', BoxElement extends HTMLElement = HTMLDivElement> = {
  /**
   * 陰影高度等級
   * @default 2
   */
  elevation?: ElevationLevel
  /**
   * 陰影方向
   * @default 'bottom'
   */
  shadowDirection?: ShadowDirection
  /**
   * 圓角半徑
   * @default 8
   */
  borderRadius?: 0 | 4 | 8
  /**
   * 是否顯示邊框
   */
  hasBorder?: boolean
  /**
   * 是否顯示左側強調邊條
   */
  highlighted?: boolean
  /**
   * 子元素
   */
  children?: ReactNode
  /**
   * 遮罩顏色，會在內容上方疊加一層半透明遮罩
   */
  mask?: string
} & BoxProps<RootComponent, BoxElement>

/**
 * Paper 是具有陰影效果的容器元件，常用於卡片、彈出層等表面。
 * 基於 Box 構建，額外提供 elevation（陰影高度）和 borderRadius 等屬性。
 *
 * @example
 * ```tsx
 * <Paper elevation={4} borderRadius={8} padding={4}>
 *   <Text>卡片內容</Text>
 * </Paper>
 * ```
 */
export const Paper = <RootComponent extends ElementType = 'div', BoxElement extends HTMLElement = HTMLDivElement>(props: PaperProps<RootComponent, BoxElement>) => {
  const {
    component = 'div',
    rootStyle,
    borderRadius = 8,
    elevation = 2,
    shadowDirection = 'bottom',
    children,
    hasBorder,
    highlighted,
    mask,
    ...rest
  } = props

  return (
    <Box
      component={component}
      rootStyle={[
        baseStyles.root,
        shadowDirection === 'top' && shadowVariants.top(elevation),
        shadowDirection === 'bottom' && shadowVariants.bottom(elevation),
        hasBorder && baseStyles.border,
        highlighted && baseStyles.highlighted,
        rootStyle,
      ]}
      borderRadius={borderRadius}
      {...rest}
    >
      {mask && <div {...stylex.props(baseStyles.mask(mask, borderRadius))} />}
      {children}
    </Box>
  )
}
